import React from "react";
import PropTypes from "prop-types";
import { Accordion, Fade } from "react-bootstrap";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, {
  Pagination,
  Scrollbar,
  Mousewheel,
  Keyboard,
} from "swiper";

import { CONTENT_PROPS } from "../shared/constants";

import * as actions from "./actions";
import * as hooks from "./hooks";
import reducer, { INITIAL_STATE } from "./reducer";
import "./styles.scss";
import WithErrorBoundary from "../../HOCs/WithErrorBoundary";
import EPGProgramList from "./components/EPGProgramList";
import EPGProgramItem from "./components/EPGProgramItem";
import { isToday, isWithinInterval } from "date-fns";
import EPGDropdownButton from "./components/EPGDropdownButton";
import EPGChannelList from "./components/EPGChannelList";
import EPGTimeList from "./components/EPGTimeList";
import EPGProgramItemBody from "./components/EPGProgramItemBody";
import classNames from "../../utils/classnameUtils";
import EPGSkeleton from "./components/EPGSkeleton";
import ContentCard from "../ContentCard";

// install Swiper modules
SwiperCore.use([Pagination, Scrollbar, Mousewheel, Keyboard]);

function EPGGrid({
  content,
  translationTexts,
  daysAfter = 7,
  daysBefore = 2,
  daysMaxRange = 15,
  contentNoImageProps = {
    backgroundColorOne: "#1C1C1C",
    backgroundColorTwo: "#343434",
  },
  centeredMode = false,
  isLoading = true,
  imageMode = "THUMB",
  initialSelectedDay = Date.now(),
  locale = "es",
  onChangeDayCallback = (dayItem) =>
    console.log(">> Selected dayItem:", dayItem),
  onSelectedProgramItemCallback = (programItem) =>
    console.log(">> Selected programItem:", programItem),
  onClickContentSelected = (contentSelected) =>
    console.log(">> Selected contentSelected:", contentSelected),
  programItemHeaderComponent = "COMPONENT_ONE",
  isContentSelected = true,
  isImgProgramItemBody = false,
  isDateInBody = true,
  swiperBreakpoints = {
    "@0.00": {
      slidesPerView: 3,
    },
    "@0.75": {
      slidesPerView: 3,
    },
    "@1.00": {
      slidesPerView: 4,
    },
    "@1.50": {
      slidesPerView: 5,
    },
  },
  showPlayIcon = true,
  enableEpgDropdown = true,
}) {
  const [firstSwiper, setFirstSwiper] = React.useState(null);
  const [collapseExpandedState, setCollapseExpandedState] = React.useState({
    isCollapseExpanded: false,
    accordionCollapseOffsetHeight: 0,
    index: 0,
    itemsLength: 0,
  });
  const containerListRef = React.useRef(null);

  // store swiper instances
  const [state, dispatch] = React.useReducer(reducer, {
    ...INITIAL_STATE,
    selectedDay: initialSelectedDay,
  });

  //#region //// HOOKS ////
  hooks.useContentEPGInfo(
    content,
    state.selectedDay,
    state.inputSearch,
    dispatch
  );
  hooks.useContentEPGFormatTime(state.selectedDay, dispatch);
  hooks.useCurrentTime(state.swiperVirtualSizePxWidth, dispatch);
  const [
    scrollOne,
    scrollTwo,
    scrollTab,
    handleScrollOne,
    handleScrollTwo,
    handleScrollTab,
  ] = hooks.useScrollSync(containerListRef, collapseExpandedState);
  hooks.useScrollToUpInSelectDay(
    state.selectedDay,
    dispatch,
    scrollOne,
    scrollTwo
  );
  //#endregion

  /* React.useEffect(()=>{
     const {to,totalMilliseconds} = informationOfTheDay(Date.now())
     const position = 1/totalMilliseconds * differenceInMilliseconds(to, Date.now()); 
     //firstSwiper?.slideTo(position, 500)
   },[currentTime])*/

  //-----------------------------------------------------
  const handleOnSelectDay = (dayItem) => {
    onChangeDayCallback && onChangeDayCallback(dayItem);
    dispatch(actions.changeCalendarDay(dayItem.epochDate));
  };

  //-----------------------------------------------------

  const handleOnSelectedProgramItem = (
    selectedProgramItem,
    selectedEpgId,
    isSameProgramItem
  ) => {
    if (isSameProgramItem) {
      onSelectedProgramItemCallback &&
        onSelectedProgramItemCallback({
          selectedEpgId: null,
          selectedProgramItem: null,
        });
      dispatch(actions.deselectedProgramItem());
    } else {
      onSelectedProgramItemCallback &&
        onSelectedProgramItemCallback({
          selectedEpgId: selectedEpgId,
          selectedProgramItem: selectedProgramItem,
        });
      dispatch(
        actions.selectedProgramItem({
          selectedEpgId: selectedEpgId,
          selectedProgramItem: selectedProgramItem,
        })
      );
    }
  };

  //------------------------------------------------------------------------
  const handleTranslateTo = () => {
    if (!isToday(state.selectedDay)) {
      handleOnSelectDay({
        epochDate: Date.now(),
      });
    }
    const translatePx = state.currentTime.swiperPxWidthOnAllSlides;
    const epgWidth = centeredMode
      ? parseFloat(
          document.querySelector(".epgGridList_epg_containerTwo").offsetWidth
        ) / 2.3
      : 100;
    firstSwiper?.translateTo(-(translatePx - epgWidth), 500);
  };

  const onChangeBroadcastLive = (bool) => {
    dispatch(actions.changeBroadcastLive(bool));
  };

  const onClickBroadcast = (event, bool) => {
    event?.preventDefault();
    if (bool) handleTranslateTo();
    dispatch(actions.changeBroadcastLive(bool));
  };

  React.useEffect(() => {
    if (state.broadcastLive && state.currentTime.swiperPxWidthOnAllSlides > 0) {
      handleTranslateTo();
    }
  }, [state.currentTime.swiperPxWidthOnAllSlides]);
  //------------------------------------------------------------------------

  const [channelHelp, setChannelHelp] = React.useState({
    selectedEpgId: null,
    selectedProgramItem: null,
    item: null,
  });

  const handleOnSelectedProgramChannel = (selectedEpgId) => {
    handleTranslateTo();
    const item = content.find((e) => e.epgId === selectedEpgId);
    const selectedProgramItem = item.epg.find((e) => {
      if (e?.startTime && e?.endTime) {
        return isWithinInterval(Date.now(), {
          start: new Date(e.startTime),
          end: new Date(e.endTime),
        });
      }
    });
    setChannelHelp({
      selectedEpgId: selectedEpgId,
      selectedProgramItem: selectedProgramItem,
      item: item,
    });
  };

  React.useEffect(() => {
    if (
      isToday(state.selectedDay) &&
      channelHelp.selectedEpgId &&
      channelHelp.selectedProgramItem
    ) {
      if (
        (state.selectedProgramItem?.externalId &&
          channelHelp.selectedProgramItem?.externalId &&
          channelHelp.selectedProgramItem.externalId ===
            state.selectedProgramItem?.externalId) ||
        (state.selectedProgramItem?.programId &&
          channelHelp.selectedProgramItem?.programId &&
          channelHelp.selectedProgramItem?.programId ===
            state.selectedProgramItem?.programId)
      ) {
        onSelectedProgramItemCallback &&
          onSelectedProgramItemCallback({
            selectedEpgId: null,
            selectedProgramItem: null,
          });
        dispatch(actions.deselectedProgramItem());
      } else {
        onSelectedProgramItemCallback &&
          onSelectedProgramItemCallback({
            selectedEpgId: channelHelp.selectedEpgId,
            selectedProgramItem: channelHelp.selectedProgramItem,
          });
        dispatch(
          actions.selectedProgramItem({
            selectedEpgId: channelHelp.selectedEpgId,
            selectedProgramItem: channelHelp.selectedProgramItem,
          })
        );
        isContentSelected &&
          onClickContentSelected({
            selectedProgramItem: state.selectedProgramItem,
            epgItemCollection: channelHelp.item,
          });
      }
      setChannelHelp({
        selectedEpgId: null,
        selectedProgramItem: null,
        item: null,
      });
    }
  }, [state.selectedDay, channelHelp]);

  //------------------------------------------------------------------------

  const handleLoadSwiperVirtualSizePxWidth = (virtualSize) =>
    dispatch(actions.loadSwiperVirtualSizePxWidth(virtualSize));

  //------------------------------------------------------------------------

  const handleOnCollapseExpanded = (
    isCollapseExpanded,
    accordionCollapseOffsetHeight,
    index
  ) => {
    setCollapseExpandedState({
      isCollapseExpanded,
      accordionCollapseOffsetHeight,
      index,
      itemsLength: state.epgData.items.length,
    });
  };
  return (
    <section className="epgGridList">
      {/*<InputGroup className="searchSection__inputGroup container-fluid">
        <FormControl
          aria-label="content search"
          aria-describedby="basic-addon2"
          className="searchSection__searchBar"
          onChange={(event) => dispatch(actions.loadInputSearch(event.target.value))}
          placeholder={('What do you want to watch?')}
          ref={null}
        />
        <InputGroup.Append
          className="searchSection__inputGroup-append"
        >
        </InputGroup.Append>
  </InputGroup>*/}
      {!isLoading ? (
        <Fade appear={true} in={true}>
          <div className="epgGridList_epg_container">
            <div className="epgGridList_epg_containerOne">
              <EPGDropdownButton
                selectedDay={state.selectedDay}
                initialSelectedDay={initialSelectedDay}
                translationTexts={translationTexts}
                daysMaxRange={daysMaxRange}
                handleOnSelectDay={handleOnSelectDay}
                locale={locale}
                daysAfter={daysAfter}
                daysBefore={daysBefore}
                turnOffLive={() =>
                  state.broadcastLive && onChangeBroadcastLive(false)
                }
              />
              <EPGChannelList
                channelList={state.epgData.items}
                imageMode={imageMode}
                contentNoImageProps={contentNoImageProps}
                activeKey={state?.selectedEpgId}
                selectedProgramItem={state.selectedProgramItem}
                scrollRef={scrollOne}
                handleOnScroll={handleScrollOne}
                handleOnSelectedProgramChannel={handleOnSelectedProgramChannel}
                onClickBroadcast={onClickBroadcast}
                selectedDay={state.selectedDay}
                broadcast={state.broadcastLive}
                enableEpgDropdown={enableEpgDropdown}
              />
              {/* <button onClick={() => firstSwiper?.slideTo(0, 500)}>first</button>
      <button onClick={() => firstSwiper?.slideTo(47, 500)}>last</button>*/}
            </div>

            <div className="epgGridList_epg_containerTwo">
              <Swiper
                spaceBetween={null} //important
                freeMode={true}
                keyboard={true}
                onSwiper={(swiper) => {
                  handleLoadSwiperVirtualSizePxWidth(swiper.virtualSize);
                  setFirstSwiper(swiper);
                }}
                onTouchMove={() =>
                  state.broadcastLive && onChangeBroadcastLive(false)
                }
                onScroll={() =>
                  state.broadcastLive && onChangeBroadcastLive(false)
                }
                navigation={false}
                observer={true}
                observeSlideChildren={true}
                onObserverUpdate={(swiper) =>
                  handleLoadSwiperVirtualSizePxWidth(swiper.virtualSize)
                }
                onResize={(swiper) =>
                  handleLoadSwiperVirtualSizePxWidth(swiper.virtualSize)
                }
                mousewheel={{
                  forceToAxis: true,
                }}
                onBreakpoint={(swiper, breakpointParams) => {
                  dispatch(
                    actions.loadSwiperSlidesPerView(
                      breakpointParams.slidesPerView
                    )
                  );
                }}
                className="epgGridList_swiper"
                breakpoints={swiperBreakpoints}
              >
                <span slot="container-start">
                  <Fade appear={true} in={true} timeout={5000}>
                    {
                      <a
                        href="/"
                        className={classNames(
                          "epgGridList_swiper_broadcast",
                          "epgGridList_swiper_broadcast_" + state.broadcastLive
                        )}
                        onClick={(event) =>
                          onClickBroadcast(event, !state.broadcastLive)
                        }
                      >
                        <i className="epgGridList_swiper_broadcast_icon bi bi-broadcast"></i>
                        <div className="epgGridList_swiper_broadcast_text">
                          {translationTexts?.live}
                        </div>
                      </a>
                    }
                  </Fade>
                  <div
                    className="epgGridList_scrool"
                    onScroll={handleScrollTab}
                    ref={scrollTab}
                  >
                    <div className="epgGridList_scrool_help" />
                  </div>
                </span>
                <div
                  slot="wrapper-start"
                  className="epgGridList_swiper_list"
                  style={{ width: "0" }}
                >
                  <div
                    className="epgGridList_swiper_container"
                    style={{
                      width: `${state.swiperVirtualSizePxWidth}px` /*state.swiperContainerPercentageWidth + '%'*/,
                    }}
                  >
                    {state?.selectedDay && isToday(state.selectedDay) && (
                      <Fade appear={true} in={true} timeout={5000}>
                        <div
                          className="epgGridList_swiper_lineTime"
                          style={{
                            left: `${
                              state.currentTime.swiperPxWidthOnAllSlides > 0 &&
                              state.currentTime.swiperPxWidthOnAllSlides
                            }px`,
                          }}
                        />
                      </Fade>
                    )}

                    <EPGTimeList
                      arrayFromTime={state.timeListInDay}
                      swiperSlideLength={state.swiperSlideLength}
                      slidesPerView={state.swiperSlidesPerView}
                      firstSwiper={firstSwiper}
                      turnOffLive={() =>
                        state.broadcastLive && onChangeBroadcastLive(false)
                      }
                    />
                    <div
                      className="epgGridList_swiper_containerList"
                      onScroll={handleScrollTwo}
                      ref={scrollTwo}
                    >
                      <Accordion
                        className="epgGridList_swiper_containerList_accordion"
                        activeKey={state?.selectedEpgId}
                        defaultActiveKey={state?.selectedEpgId}
                        ref={containerListRef}
                      >
                        {state.epgData.items?.length > 0 &&
                          state.epgData.items?.map((e, index) => (
                            <EPGProgramList
                              key={`EPGProgramList-${index}-${state.selectedDay}-${e.epgId}`}
                              selectedDay={state.selectedDay}
                              epgItemCollection={e}
                              selectedEpgId={state.selectedEpgId}
                              setCollapseExpanded={(
                                bool,
                                accordionCollapseHeight
                              ) =>
                                handleOnCollapseExpanded(
                                  bool,
                                  accordionCollapseHeight,
                                  index
                                )
                              }
                              renderItem={(
                                item,
                                eventKey,
                                percentageOfTime,
                                startLineTime,
                                epgItemCollection,
                                keyNoContent,
                                selectedEpgId
                              ) => (
                                <EPGProgramItem
                                  programItemHeaderComponent={
                                    programItemHeaderComponent
                                  }
                                  content={item}
                                  epgItemCollection={epgItemCollection}
                                  translationTexts={translationTexts}
                                  selectedProgramItem={
                                    state.selectedProgramItem
                                  }
                                  startLineTime={startLineTime}
                                  currentTime={state.currentTime?.date}
                                  selectedDay={state.selectedDay}
                                  eventKey={eventKey}
                                  handleOnSelectedProgramItem={
                                    handleOnSelectedProgramItem
                                  }
                                  percentageOfTime={percentageOfTime}
                                  firstSwiper={firstSwiper}
                                  key={
                                    item?.cid || item?.programId || keyNoContent
                                  }
                                  selectedEpgId={selectedEpgId}
                                  onClickContentSelected={
                                    onClickContentSelected
                                  }
                                  enableEpgDropdown={enableEpgDropdown}
                                />
                              )}
                              renderItemBody={(epgItemCollection) => (
                                <EPGProgramItemBody
                                  firstSwiper={firstSwiper}
                                  translationTexts={translationTexts}
                                  selectedProgramItem={
                                    state.selectedProgramItem
                                  }
                                  onClickContentSelected={
                                    onClickContentSelected
                                  }
                                  epgItemCollection={epgItemCollection}
                                  currentTime={state.currentTime?.date}
                                  isContentSelected={isContentSelected}
                                  isImgProgramItemBody={isImgProgramItemBody}
                                  isDateInBody={isDateInBody}
                                  contentNoImageProps={contentNoImageProps}
                                  ContentCard={ContentCard}
                                  showPlayIcon={showPlayIcon}
                                />
                              )}
                              enableEpgDropdown={enableEpgDropdown}
                            />
                          ))}
                      </Accordion>
                    </div>
                  </div>
                </div>
                {state.timeListInDay.map((e, index) => (
                  <SwiperSlide
                    key={`timeListInDay-${index}`}
                    style={{ visibility: "hidden", opacity: 0 }}
                    className="swiperSlide-help"
                  />
                ))}
              </Swiper>
            </div>
          </div>
        </Fade>
      ) : (
        <EPGSkeleton contentNoImageProps={contentNoImageProps} />
      )}
    </section>
  );
}

EPGGrid.propTypes = {
  content: PropTypes.array,
  daysAfter: PropTypes.number,
  daysBefore: PropTypes.number,
  daysMaxRange: PropTypes.number,
  displayedItemHeightInPx: PropTypes.number,
  displayedItemsNum: PropTypes.number,
  epgID: PropTypes.string,
  epgItems: PropTypes.array,
  imageMode: PropTypes.oneOf(["POSTER", "THUMB"]),
  initialSelectedDay: PropTypes.number,
  onChangeDayHandler: PropTypes.func,
  title: PropTypes.string,
  unityToken: PropTypes.object,
  showPlayIcon: PropTypes.bool,
  enableEpgDropdown: PropTypes.bool,
};

export default WithErrorBoundary(EPGGrid);
